import { Checkbox, Modal, Text, TextInput } from "@mantine/core";
import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import MultiSelect from "src/components/shared/_FormInputs/MultiSelect";
import Select from "src/components/shared/_FormInputs/Select";
import { Button } from "src/components/shared/Button/Button";
import { usePostApi } from "src/hooks";
import { Flex } from "src/styled";
import { deepClone, getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import { validateTankLimitOrder } from "./tankLimits.utils";
import {
  ETankPlanLogbook,
  type ITankPlan,
  orbs,
  tankTypes,
} from "./tankPlans.model";
import { useCanEditTankLimits } from "./useCanEditTankLimits";

interface Props {
  opened: boolean;
  close: () => void;
  values?: ITankPlan;
  refetch: () => void;
}

const capacityUnits = ["MT", "m³"];
const logbooks: Array<"bwl" | "mfsbB" | "mfsbA"> = ["bwl", "mfsbA", "mfsbB"];
const limitFields = [
  { name: "limits.hh" as const, label: "HH" },
  { name: "limits.h" as const, label: "H" },
  { name: "limits.l" as const, label: "L" },
  { name: "limits.ll" as const, label: "LL" },
];

const TankPlansAddModal = ({ opened, close, values, refetch }: Props) => {
  const { post } = usePostApi();
  const canEditLimits = useCanEditTankLimits();

  const defaultValues = values ?? {
    name: "",
    type: "",
    orb: ["*"],
    bwl: false,
    mfsbA: false,
    mfsbB: false,
    capacity: {
      value: "",
      unit: "m³",
    },
    limits: {
      hh: "",
      h: "",
      l: "",
      ll: "",
    },
  };
  const methods = useForm({
    values: {
      ...defaultValues,
      limits: {
        hh: "",
        h: "",
        l: "",
        ll: "",
        ...defaultValues.limits,
      },
    },
  });

  const capacityUnit = methods.watch("capacity.unit");
  const prevOrbRef = useRef<string[]>(methods.getValues("orb"));

  useEffect(() => {
    prevOrbRef.current = methods.getValues("orb");
  });
  const handleOrbChange = (newValue: string[]) => {
    const prevOrb = deepClone(prevOrbRef.current);

    const sortedItems = newValue.sort((a, b) => {
      const indexA = orbs.findIndex(el => el.value === a);
      const indexB = orbs.findIndex(el => el.value === b);
      return indexA - indexB;
    });
    prevOrbRef.current = newValue;

    if (prevOrb.includes("*") && newValue.length > 1) {
      methods.setValue(
        "orb",
        sortedItems.filter(el => el !== "*"),
      );
    } else {
      methods.setValue("orb", sortedItems);
    }
  };
  const handleSubmit = async (data: any) => {
    // Disabled RHF fields are omitted from submit; preserve existing limits.
    if (!canEditLimits) {
      data.limits = values?.limits ?? data.limits;
    }
    const limitError = validateTankLimitOrder(data.limits, data.capacity);
    if (limitError) {
      const formField =
        limitError.field === "capacity"
          ? "capacity.value"
          : (`limits.${limitError.field}` as const);
      methods.setError(formField, {
        type: "manual",
        message: limitError.message,
      });
      return;
    }
    data.yachtid = getYachtId();
    await post({
      request: "save-tank",
      body: {
        params: { ...data },
      },
      onSuccess: () => {
        Notification(
          "Success",
          `Tank ${values ? "edited" : "added"} successfully`,
          "green",
        );
        refetch();
        handleClose();
      },
    });
  };

  const handleClose = () => {
    close();
    setTimeout(() => {
      methods.reset();
    }, 200);
  };
  return (
    <Modal
      title="Add Tank Plan"
      opened={opened}
      onClose={handleClose}
      size="lg">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleSubmit)}
          style={{
            minHeight: "550px",
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
          }}>
          <Flex direction="column" gap={5}>
            <Select
              control={methods.control}
              name="type"
              data={tankTypes}
              label="Type"
              required={true}
            />
            <TextInput
              {...methods.register("name")}
              label="Name"
              required={true}
            />
            <Flex gap={10}>
              <TextInput
                type="number"
                step="0.001"
                {...methods.register("capacity.value")}
                label="Capacity"
                required={true}
                style={{ flex: 1 }}
                error={methods.formState.errors.capacity?.value?.message}
              />
              <Select
                control={methods.control}
                name="capacity.unit"
                data={capacityUnits}
                label="Unit"
                style={{ maxWidth: "100px" }}
              />
            </Flex>
            <Text fw={200} mt={5}>
              Limits ({capacityUnit}){!canEditLimits ? " — view only" : ""}
            </Text>
            <Flex gap={10}>
              {limitFields.map(({ name, label }) => {
                const limitKey = name.split(".")[1] as "hh" | "h" | "l" | "ll";
                return (
                  <TextInput
                    key={name}
                    type="number"
                    step="0.001"
                    {...methods.register(name)}
                    label={label}
                    disabled={!canEditLimits}
                    style={{ flex: 1 }}
                    error={methods.formState.errors.limits?.[limitKey]?.message}
                  />
                );
              })}
            </Flex>

            <MultiSelect
              control={methods.control}
              name="orb"
              data={orbs}
              label="Oil Record Book"
              multiple={true}
              onChange={handleOrbChange}
            />
            <Flex direction="column" gap={5} mt={15}>
              <Text fw={200}>Additionally assigned to:</Text>
              {logbooks.map((el, i) => (
                <Flex
                  key={`checkbox-add-${i}`}
                  justify="space-between"
                  w="85%"
                  ml={10}>
                  <Text>{ETankPlanLogbook[el]}</Text>
                  <Checkbox
                    {...methods.register(el)}
                    checked={methods.watch(el)}
                  />
                </Flex>
              ))}
            </Flex>
          </Flex>
          <Flex justify="space-between" gap={10} mt={40}>
            <Button variant="outline" color="red" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">{values ? "Save" : "Add"}</Button>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
export default TankPlansAddModal;
